import React from "react";
import { Badge, Col, OverlayTrigger, ProgressBar, Row, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useRouteLoaderData } from "react-router-dom";

import { GamePublicationState, GamePublicationWishlistState } from "@/const";
import { Awesome } from "@components/Helpers";
import { url_reverse } from "@components/Utils";

export function DrawProgressBar({ labels, active, now = 25 }) {
    return (
        <Row>
            <Col md={12}>
                <ProgressBar className="my-2">
                    {labels.map((lbl, i) => {
                        return (
                            <ProgressBar
                                key={`progress_bar_${i}`}
                                striped
                                label={lbl}
                                variant={active === i + 1 ? "warning" : active <= i ? "secondary" : "success"}
                                now={now}
                                className={active === i + 1 ? "text-dark" : ""}
                            />
                        );
                    })}
                </ProgressBar>
            </Col>
        </Row>
    );
}

export function GamePublicationStateProgressBar() {
    const { t } = useTranslation();
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");

    const labels = [
        t("Конфигурация"),
        t("Техническая модерация проекта"),
        t("Маркетинговая модерация проекта"),
        t("Модерация проекта пройдена"),
    ];

    let active = 0;
    switch (gameCommonInfo.game.publication_state) {
        case GamePublicationState.CONFIGURATION:
        case GamePublicationState.CONFIGURATION_MARKETING:
            active = 1;
            break;
        case GamePublicationState.TESTING:
            active = 2;
            break;
        case GamePublicationState.TESTING_MARKETING:
            active = 3;
            break;
        case GamePublicationState.DONE:
            active = 100;
            break;
    }

    return <DrawProgressBar labels={labels} active={active} />;
}

export function GamePublicationWishlistStateProgressBar() {
    const { t } = useTranslation();
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");

    const labels = [
        t("Конфигурация"),
        t("Техническая модерация страницы"),
        t("Маркетинговая модерация страницы"),
        t("Модерация страницы пройдена"),
    ];

    let active = 0;
    switch (gameCommonInfo.game.publication_wishlist_state) {
        case GamePublicationWishlistState.CONFIGURATION:
        case GamePublicationWishlistState.CONFIGURATION_MARKETING:
            active = 1;
            break;
        case GamePublicationWishlistState.TESTING_MARKETING:
            active = 3;
            break;
        case GamePublicationWishlistState.DONE:
            active = 100;
            break;
    }

    return <DrawProgressBar labels={labels} active={active} />;
}

export function GameUnpublishRelationship({ relationship }) {
    const { t } = useTranslation();
    return (
        <div className="dmg-callout dmg-callout-warning my-3">
            <h6 className="mb-3 text-center">
                {relationship.bundles.length +
                    relationship.in_bundles.length +
                    relationship.dlc.length +
                    relationship.demo.length >
                0
                    ? t("Проект и его связи")
                    : t("Проект не имеет связей с другими продуктами")}
            </h6>

            <Row>
                {relationship.bundles.length + relationship.in_bundles.length > 0 && (
                    <Col>
                        {relationship.bundles.length > 0 && (
                            <div className="mb-3">
                                <h6>{t("Наборы будут сняты с публикации")}</h6>
                                {relationship.bundles.map((el) => {
                                    return (
                                        <div key={`${el.source_id}_${el.target_id}`}>
                                            <a
                                                href={url_reverse("games:admin", { game_id: el.target_id })}
                                                target="_blank"
                                            >
                                                {el.target_obj.base_locale_object.name}
                                            </a>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        {relationship.in_bundles.length > 0 && (
                            <div className="mb-3">
                                <h6>{t("Проект будет удален из состава наборов")}</h6>
                                {relationship.in_bundles.map((el) => {
                                    return (
                                        <div key={`${el.source_id}_${el.target_id}`}>
                                            <a
                                                href={url_reverse("games:admin", { game_id: el.source_id })}
                                                target="_blank"
                                            >
                                                {el.source_obj.base_locale_object.name}
                                            </a>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </Col>
                )}
                {relationship.dlc.length > 0 && (
                    <Col>
                        <div className="mb-3">
                            <h6>{t("DLC будут сняты с публикации")}</h6>
                            {relationship.dlc.map((el) => {
                                return (
                                    <div key={`${el.source_id}_${el.target_id}`}>
                                        <a href={url_reverse("games:admin", { game_id: el.target_id })} target="_blank">
                                            {el.target_obj.base_locale_object.name}
                                        </a>
                                    </div>
                                );
                            })}
                        </div>
                    </Col>
                )}
                {relationship.demo.length > 0 && (
                    <Col>
                        <div className="mb-3">
                            <h6>{t("Демо игра будет снята с публикации")}</h6>
                            {relationship.demo.map((el) => {
                                return (
                                    <div key={`${el.source_id}_${el.target_id}`}>
                                        <a href={url_reverse("games:admin", { game_id: el.target_id })} target="_blank">
                                            {el.target_obj.base_locale_object.name}
                                        </a>
                                    </div>
                                );
                            })}
                        </div>
                    </Col>
                )}
                {relationship.playtest.length > 0 && (
                    <Col>
                        <div className="mb-3">
                            <h6>{t("Playtest будет снят с публикации")}</h6>
                            {relationship.playtest.map((el) => {
                                return (
                                    <div key={`${el.source_id}_${el.target_id}`}>
                                        <a href={url_reverse("games:admin", { game_id: el.target_id })} target="_blank">
                                            {el.target_obj.base_locale_object.name}
                                        </a>
                                    </div>
                                );
                            })}
                        </div>
                    </Col>
                )}
                {relationship.editions.length > 0 && (
                    <Col>
                        <div className="mb-3">
                            <h6>{t("Издание будет снято с публикации")}</h6>
                            {relationship.editions.map((el) => {
                                return (
                                    <div key={`${el.source_id}_${el.target_id}`}>
                                        <a href={url_reverse("games:admin", { game_id: el.target_id })} target="_blank">
                                            {el.target_obj.base_locale_object.name}
                                        </a>
                                    </div>
                                );
                            })}
                        </div>
                    </Col>
                )}
            </Row>
        </div>
    );
}

export function GameToBrowserButton({ game, asButton, withLabel }) {
    const { t } = useTranslation();
    const commonInfo = useRouteLoaderData("commonInfo");
    const slug = game?.parent && !game?.dlc ? game?.parent?.slug : game.slug;

    const dom = commonInfo.domain_showcase;
    return (
        <OverlayTrigger
            placement="top"
            overlay={
                <Tooltip id={`tooltip-${game.slug}`}>
                    {t(
                        "Предпросмотр страницы игры в браузере. Для корректного отображения необходимо заполнить раздел «Страны»"
                    )}
                </Tooltip>
            }
        >
            <a
                className={asButton ? "btn btn-sm btn-info" : "mx-2"}
                href={`https://${dom}/play/game/${slug}/`}
                target="_blank"
            >
                <Awesome type="globe" />
                {asButton && withLabel && <> {t("В браузер")}</>}
            </a>
        </OverlayTrigger>
    );
}

export function GameToGameCenterButton({ game, asButton, withLabel }) {
    const { t } = useTranslation();
    const gcId = game.parent && !game?.dlc ? game.parent.gc_id : game.gc_id;

    return (
        gcId && (
            <OverlayTrigger
                placement="top"
                overlay={
                    <Tooltip id={`tooltip-gc-${gcId}`}>
                        {t(
                            "Предпросмотр страницы игры в Игровом Центре. Для корректного отображения необходимо заполнить раздел «Страны»"
                        )}
                    </Tooltip>
                }
            >
                <a
                    className={asButton ? "btn btn-sm btn-info" : ""}
                    href={`mailrugames://show/${gcId}/?mode=store`}
                    target="_blank"
                >
                    <Awesome type="gamepad" />
                    {asButton && withLabel && <> {t("В Игровой Центр")}</>}
                </a>
            </OverlayTrigger>
        )
    );
}

export const DeveloperLink = ({ developer }) => {
    return developer && <Link to={url_reverse("devs:edit", { dev_id: developer.id })}>{developer.name}</Link>;
};

export const DocumentStateBadge = ({ state, text }) => {
    let variant = "";
    switch (state) {
        case "declined":
            variant = "danger";
            break;
        case "sent":
            variant = "warning";
            break;
        case "checking":
            variant = "secondary";
            break;
        case "signed":
            variant = "success";
            break;
        default:
            variant = "light";
    }
    return <Badge variant={variant}>{text}</Badge>;
};
